export type TenantBranding = {
  logo?: string;
  logoLight?: string;
  favicon?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  fontFamily?: string;
  borderRadius?: number;
};

export type TenantConfig = {
  id: string;
  name?: string;
  displayName?: string;
  domain?: string;
  layout?: string;
  theme?: string;
  typography?: string;
  icons?: string;
  branding?: TenantBranding;
  features?: Record<string, any>;
  integrations?: Record<string, any>;
  contact?: Record<string, any>;
};

const DEFAULT_TENANT_ID = 'c5232f40-4abe-5412-a540-3be5e17418be';

// Cache-buster for static tenant assets (logos/favicon) to avoid stale images
// after updating files with the same path in `public/tenants/...`.
// Stable for a single page load, refresh updates it.
const TENANT_ASSET_CACHE_BUST_KEY = String(Date.now());

function withTenantAssetCacheBust(url?: string): string | undefined {
  if (!url) return url;
  const trimmed = url.trim();
  if (!trimmed) return trimmed;

  // Only apply to same-origin tenant asset paths.
  // If a tenant uses a CDN/absolute URL, keep it unchanged.
  if (!trimmed.startsWith('/tenants/')) return trimmed;

  return `${trimmed}${trimmed.includes('?') ? '&' : '?'}v=${encodeURIComponent(TENANT_ASSET_CACHE_BUST_KEY)}`;
}

function normalizeTenantId(value: unknown): string {
  if (typeof value !== 'string') return DEFAULT_TENANT_ID;
  const trimmed = value.trim();
  if (!trimmed) return DEFAULT_TENANT_ID;

  // Guard against build-time placeholders not being substituted.
  // Example observed in dev: "%VITE_TENANT_ID%" -> encoded as "%25VITE_TENANT_ID%25".
  const upper = trimmed.toUpperCase();

  // If the value still contains the env var name (raw or URL-encoded), treat it as unset.
  // This prevents requests like `/tenants/%25VITE_TENANT_ID%25/config.json`.
  if (
    upper.includes('VITE_TENANT_ID') ||
    upper.includes('EXPO_PUBLIC_TENANT_ID') ||
    upper.includes('PUBLIC_DEFAULT_TENANT_ID')
  ) {
    return DEFAULT_TENANT_ID;
  }

  if (
    (trimmed.startsWith('%') && trimmed.endsWith('%')) ||
    trimmed.includes('${') ||
    trimmed.includes('%25') ||
    trimmed.includes('%7B') ||
    trimmed.includes('%7D')
  ) {
    if (
      upper.includes('VITE_TENANT_ID') ||
      upper.includes('EXPO_PUBLIC_TENANT_ID') ||
      upper.includes('PUBLIC_DEFAULT_TENANT_ID')
    ) {
      return DEFAULT_TENANT_ID;
    }
  }

  if (upper === 'DEFAULT' || upper === 'UNDEFINED' || upper === 'NULL') {
    return DEFAULT_TENANT_ID;
  }

  return trimmed;
}

export function getTenantIdFromRuntime(): string {
  try {
    if (typeof window !== 'undefined' && window.location?.search) {
      const params = new URLSearchParams(window.location.search);
      const fromQuery =
        params.get('tenantId') ||
        params.get('tenant_id') ||
        params.get('tenant') ||
        params.get('providerId') ||
        params.get('provider_id') ||
        params.get('provider');
      if (typeof fromQuery === 'string' && fromQuery.trim()) {
        return normalizeTenantId(fromQuery);
      }
    }

    const fromGlobal = (globalThis as any)?.__XEDU_PUBLIC_CONFIG__?.tenantId;
    if (typeof fromGlobal === 'string' && fromGlobal.trim()) {
      return normalizeTenantId(fromGlobal);
    }

    const anyGlobal = globalThis as any;
    const env = anyGlobal?.process?.env || {};
    return normalizeTenantId(
      env.EXPO_PUBLIC_TENANT_ID ||
        env.VITE_TENANT_ID ||
        env.PUBLIC_DEFAULT_TENANT_ID ||
        DEFAULT_TENANT_ID
    );
  } catch {
    return DEFAULT_TENANT_ID;
  }
}

export function getTenantConfigUrl(tenantId: string): string {
  const normalized = normalizeTenantId(tenantId);
  return `/tenants/${encodeURIComponent(normalized)}/config.json`;
}

export async function loadTenantConfig(tenantId: string): Promise<TenantConfig | null> {
  if (typeof fetch !== 'function') return null;

  const url = getTenantConfigUrl(tenantId);
  try {
    const resp = await fetch(url, { cache: 'no-store' });
    if (!resp.ok) return null;
    return (await resp.json()) as TenantConfig;
  } catch {
    return null;
  }
}

export function applyTenantBrandingToDocument(tenant?: TenantConfig | null): void {
  try {
    if (typeof document === 'undefined') return;

    const branding = tenant?.branding;

    const title = tenant?.displayName || tenant?.name;
    // Keep the base HTML title (e.g. "AI Assistant") unless it's blank.
    if (title && (!document.title || !document.title.trim())) {
      document.title = title;
    }

    const faviconHref = branding?.favicon;
    if (faviconHref) {
      const cacheBusted = `${faviconHref}${faviconHref.includes('?') ? '&' : '?'}v=${Date.now()}`;

      const existing =
        document.querySelector<HTMLLinkElement>('link#app-favicon') ||
        document.querySelector<HTMLLinkElement>('link[rel="icon"]') ||
        document.querySelector<HTMLLinkElement>('link[rel="shortcut icon"]');

      const parent = (existing?.parentNode || document.head) as HTMLElement;
      try {
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      } catch {
        // ignore
      }

      const link = document.createElement('link');
      link.id = 'app-favicon';
      link.rel = 'icon';
      link.type = 'image/png';
      link.href = cacheBusted;
      parent.appendChild(link);

      const shortcut = document.createElement('link');
      shortcut.rel = 'shortcut icon';
      shortcut.type = 'image/png';
      shortcut.href = cacheBusted;
      parent.appendChild(shortcut);
    }

    const themeColor = branding?.primaryColor;
    if (themeColor) {
      const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
      if (meta) meta.content = themeColor;
    }
  } catch {
    // ignore
  }
}

function parseBorderRadius(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function isUuidLike(value: string): boolean {
  const s = (value || '').trim();
  if (!s) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

export function tenantConfigToChatConfig(tenant: TenantConfig | null, fallbackTenantId: string) {
  // Only use tenant.id if it looks like a UUID.
  const rawId = (tenant?.id || '').trim();
  const tenantId = isUuidLike(rawId) ? rawId : fallbackTenantId;
  const branding = tenant?.branding;

  return {
    tenantId,
    branding: branding
      ? {
          ...branding,
          logo: withTenantAssetCacheBust(branding.logo),
          logoLight: withTenantAssetCacheBust(branding.logoLight),
          favicon: withTenantAssetCacheBust(branding.favicon),
          borderRadius: parseBorderRadius(branding.borderRadius),
        }
      : undefined,
    theme: {
      primaryColor: branding?.primaryColor,
      secondaryColor: branding?.secondaryColor,
      accentColor: branding?.accentColor,
      fontFamily: branding?.fontFamily,
    },
  };
}

export function createPaperThemeFromTenantBranding(
  baseTheme: any,
  branding?: TenantBranding
): any {
  const primary = branding?.primaryColor || '#146B55';
  const secondary = branding?.secondaryColor || '#1F6F8B';
  const tertiary = branding?.accentColor || '#D99A16';
  const background = branding?.backgroundColor || '#F7F3EA';

  const merged = {
    ...baseTheme,
    roundness: parseBorderRadius(branding?.borderRadius) ?? baseTheme.roundness ?? 10,
    colors: {
      ...baseTheme.colors,
      primary,
      onPrimary: '#FFFFFF',
      primaryContainer: '#DDEFE8',
      onPrimaryContainer: '#0B473A',
      secondary,
      onSecondary: '#FFFFFF',
      secondaryContainer: '#DDEEF4',
      onSecondaryContainer: '#123F52',
      tertiary,
      onTertiary: '#FFFFFF',
      tertiaryContainer: '#F8E8BC',
      onTertiaryContainer: '#8A5A05',
      background,
      onBackground: '#24322D',
      surface: '#FFFFFF',
      surfaceVariant: '#FFFCF5',
      surfaceDisabled: '#EDE5D7',
      onSurface: '#24322D',
      onSurfaceVariant: '#65736E',
      onSurfaceDisabled: '#817A70',
      outline: '#D9D1C4',
      outlineVariant: '#EDE5D7',
      error: '#C44D3D',
      onError: '#FFFFFF',
    },
  };

  return merged;
}
